import React, { useState, useEffect, useCallback } from 'react';
import {
  X, ArrowRight, ArrowLeft, Play,
  Flame, MessageSquareQuote, Shirt, Dumbbell,
  UtensilsCrossed, Layers, Building2, Wallet, Bot, Check,
} from 'lucide-react';

/**
 * First-run product tour.
 *
 * Eight modules is a lot to meet at once, and most of them only make sense
 * once you know they refresh on their own overnight — so each card says what
 * the module is for and when it updates. Shown once, replayable from Settings.
 */

const DONE_KEY = 'rdi_tour_v1_done';

export function tourSeen() {
  try { return localStorage.getItem(DONE_KEY) === '1'; } catch (_) { return true; }
}

function markSeen() {
  try { localStorage.setItem(DONE_KEY, '1'); } catch (_) { /* private mode */ }
}

const STEPS = [
  {
    icon: Play,
    tag: 'Welcome',
    title: 'Your Operating System',
    body: 'Eight modules cover your day — motivation, athletes, style, training, food, and deals. '
      + 'They rebuild themselves overnight, so the dashboard is already current when you open it.',
    meta: 'Takes about a minute',
  },
  {
    icon: Flame,
    tag: 'Module 01',
    title: 'Daily Motivation',
    body: 'A fresh set of motivational clips every morning, picked and ordered for you. '
      + 'Nothing repeats — the last 45 days are tracked so you never see the same clip twice.',
    meta: 'Rebuilds at 2:00 AM',
  },
  {
    icon: MessageSquareQuote,
    tag: 'Module 02',
    title: 'Athlete Brief',
    body: 'Pick a quotation, then pick one of your 88 athletes. Send it straight from your own '
      + 'WhatsApp or Messages — it opens the chat with the message already written.',
    meta: 'Search or filter by sport',
  },
  {
    icon: Shirt,
    tag: 'Module 03',
    title: 'Fashion Feed',
    body: 'Menswear looks pulled from Instagram overnight and scored on style. '
      + 'Only the strongest make the feed, sorted by occasion.',
    meta: 'Rebuilds at 3:00 AM',
  },
  {
    icon: Dumbbell,
    tag: 'Module 04',
    title: 'Workout Plan',
    body: "Today's session built around the day's split, with sets, reps, rest and form cues. "
      + 'Each exercise links to a demo video.',
    meta: 'Rebuilds at 4:00 AM',
  },
  {
    icon: UtensilsCrossed,
    tag: 'Module 05',
    title: 'Meal Plans',
    body: 'Three full days of food — Lean, Performance and Comfort — with real macros. '
      + 'Ask the assistant to swap any meal and the day recalculates itself.',
    meta: 'Rebuilds at 3:30 AM',
  },
  {
    icon: Layers,
    tag: 'Module 06',
    title: 'Outfit Combos',
    body: 'Complete looks built from your own 101 jackets, grouped by occasion. '
      + 'Tap any look for the trousers, shirt, shoes and accessories to wear with it.',
    meta: 'Rebuilds at 5:30 AM',
  },
  {
    icon: Building2,
    tag: 'Module 07',
    title: 'Deal Finder',
    body: 'Live listings in your target markets, each with a real rent estimate and a verdict. '
      + 'Weak deals are filtered out, so what you see is worth a look.',
    meta: 'Rebuilds at 4:30 AM',
  },
  {
    icon: Wallet,
    tag: 'Module 08',
    title: 'Income & Budget',
    body: 'Your private ledger, behind its own code and kept on this device only. '
      + 'Set recurring costs once and they post themselves every month.',
    meta: 'Back it up — it lives on this phone',
  },
  {
    icon: Bot,
    tag: 'Anywhere',
    title: 'Just Ask',
    body: 'The assistant button sits on every screen. Ask it anything — or tell it to change '
      + 'something, like swapping a meal or adding an athlete, and it updates the module for you.',
    meta: 'Tap the circle, bottom right',
  },
  {
    icon: Check,
    tag: 'Ready',
    title: "You're Set",
    body: 'Everything refreshes overnight, so just open it each morning. '
      + 'You can replay this tour any time from Settings.',
    meta: null,
  },
];

export default function ProductTour({ onClose }) {
  const [i, setI] = useState(0);
  const step = STEPS[i];
  const last = i === STEPS.length - 1;

  const finish = useCallback(() => {
    markSeen();
    onClose();
  }, [onClose]);

  const next = useCallback(() => {
    if (last) finish();
    else setI((n) => Math.min(n + 1, STEPS.length - 1));
  }, [last, finish]);

  const back = useCallback(() => setI((n) => Math.max(n - 1, 0)), []);

  // Arrow keys and Escape, for anyone opening this on a laptop.
  useEffect(() => {
    const onKey = (e) => {
      if (e.key === 'ArrowRight' || e.key === 'Enter') next();
      else if (e.key === 'ArrowLeft') back();
      else if (e.key === 'Escape') finish();
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [next, back, finish]);

  // The page behind must not scroll while the tour is open.
  useEffect(() => {
    const prev = document.body.style.overflow;
    document.body.style.overflow = 'hidden';
    return () => { document.body.style.overflow = prev; };
  }, []);

  const Icon = step.icon;

  return (
    <div
      role="dialog"
      aria-modal="true"
      aria-label="Product tour"
      className="fixed inset-0 z-[80] flex items-center justify-center px-5
        bg-black/80 backdrop-blur-sm safe-top safe-bottom"
    >
      <div
        key={i}
        className="w-full max-w-sm border bg-[#0d0d0d] tour-card"
        style={{ borderColor: 'rgba(201,168,76,0.35)' }}
      >
        <div className="flex items-center justify-between px-5 pt-4">
          <span className="text-[9px] tracking-[0.3em] uppercase" style={{ color: 'var(--gold)' }}>
            {step.tag}
          </span>
          <button onClick={finish} aria-label="Skip tour"
            className="p-1.5 -mr-1.5 text-gray-600 hover:text-white transition-colors">
            <X size={16} />
          </button>
        </div>

        <div className="px-5 pt-5 pb-6">
          <div
            className="w-14 h-14 flex items-center justify-center border mb-5"
            style={{ borderColor: 'rgba(201,168,76,0.3)', background: 'rgba(201,168,76,0.06)' }}
          >
            <Icon size={24} style={{ color: 'var(--gold)' }} strokeWidth={1.4} />
          </div>

          <h2 className="text-xl font-light tracking-[0.08em] uppercase leading-snug">
            {step.title}
          </h2>

          <p className="mt-3 text-[13px] text-gray-400 leading-relaxed">
            {step.body}
          </p>

          {step.meta && (
            <p className="mt-4 text-[9px] tracking-[0.2em] uppercase text-gray-600">
              {step.meta}
            </p>
          )}
        </div>

        <div className="flex items-center justify-between px-5 pb-5">
          <div className="flex items-center gap-1.5" aria-hidden="true">
            {STEPS.map((_, n) => (
              <span
                key={n}
                className="h-1 rounded-full transition-all duration-300"
                style={{
                  width: n === i ? 16 : 5,
                  backgroundColor: n === i ? 'var(--gold)' : 'rgba(255,255,255,0.18)',
                }}
              />
            ))}
          </div>

          <div className="flex items-center gap-2">
            {i > 0 && (
              <button onClick={back} aria-label="Previous"
                className="p-2.5 border border-white/12 text-gray-400 hover:text-white
                  hover:border-white/30 transition-colors">
                <ArrowLeft size={14} />
              </button>
            )}
            <button
              onClick={next}
              className="flex items-center gap-2 px-5 py-2.5 text-[10px] tracking-[0.25em]
                uppercase font-semibold transition-opacity active:opacity-80"
              style={{ backgroundColor: 'var(--gold)', color: '#000' }}
            >
              {last ? 'Finish' : 'Next'}
              {!last && <ArrowRight size={13} />}
            </button>
          </div>
        </div>
      </div>

      <p className="fixed bottom-6 left-0 right-0 text-center text-[9px] tracking-[0.2em]
        uppercase text-gray-700 pointer-events-none">
        {i + 1} of {STEPS.length}
      </p>
    </div>
  );
}
